import type { ExplorerIndexRow } from '@graphql/queries/PerceptionModelQueries';

/**
 * Turning the model's products into points on the flavor space map.
 *
 * The coordinates are UMAP output stored by the training run. They describe
 * relative position only: neighbouring points are genuinely similar, but the
 * distance between distant groups is not meaningful and the axes carry no
 * units and no direction. Nothing here derives clusters — the model does not
 * store any, so any boundary drawn would be this screen's invention.
 */

/** Why a point is drawn the way it is. Ordered by precedence. */
export type PointState = 'anchor' | 'included' | 'matched' | 'other';

export type ExplorerPoint = {
  product: string;
  x: number;
  y: number;
  state: PointState;
};

export type ExplorerPointSet = {
  points: ExplorerPoint[];
  /** rows with no usable coordinates, reported rather than silently dropped */
  unplaced: string[];
};

type Marks = {
  anchors: Set<string>;
  included: Set<string>;
  matched: Set<string>;
};

/**
 * Anchors first: they define the comparison, so their position matters most
 * even when they are also included or match the current filter. `matched`
 * loses to `included` because membership is a decision and a filter is a view.
 */
const stateOf = (product: string, marks: Marks): PointState => {
  if (marks.anchors.has(product)) return 'anchor';
  if (marks.included.has(product)) return 'included';
  if (marks.matched.has(product)) return 'matched';
  return 'other';
};

/**
 * A product whose coordinates are missing or non-finite has no position. It is
 * left off the map and named, rather than being drawn at the origin where it
 * would read as a real — and central — location.
 */
export const explorerPoints = (
  rows: ExplorerIndexRow[],
  marks: Marks,
): ExplorerPointSet => {
  const points: ExplorerPoint[] = [];
  const unplaced: string[] = [];

  rows.forEach((row) => {
    if (!row || typeof row.productName !== 'string') return;
    if (!Number.isFinite(row.umap1) || !Number.isFinite(row.umap2)) {
      unplaced.push(row.productName);
      return;
    }
    points.push({
      product: row.productName,
      x: row.umap1 as number,
      y: row.umap2 as number,
      state: stateOf(row.productName, marks),
    });
  });

  return { points, unplaced };
};

/** Draw order, so anchors and picks land on top of the crowd. */
export const SERIES_ORDER: PointState[] = [
  'other',
  'matched',
  'included',
  'anchor',
];

export const SERIES_LABEL: Record<PointState, string> = {
  anchor: 'Anchor',
  included: 'In norm set',
  matched: 'Matches tokens',
  other: 'Other products',
};

/**
 * Group into the series shape nivo expects, so colour and legend come from the
 * chart rather than from per-point styling. Empty series are dropped — an
 * always-present "Matches tokens" legend entry would imply a filter is active
 * when none is.
 */
export type PointDatum = { x: number; y: number; product: string };

export const toSeries = (
  points: ExplorerPoint[],
): Array<{ id: string; data: PointDatum[] }> =>
  SERIES_ORDER.map((state) => ({
    id: SERIES_LABEL[state],
    data: points
      .filter((p) => p.state === state)
      .map((p) => ({ x: p.x, y: p.y, product: p.product })),
  })).filter((s) => s.data.length > 0);

/**
 * The window the map draws.
 *
 * Two things force this to be computed rather than left to the chart.
 *
 * nivo's scatter plot defaults both axes to `{ type: 'linear', min: 0 }`, and
 * UMAP coordinates are routinely negative — in one real universe 72% of
 * products have a negative coordinate, and a norm set sitting in the negative
 * quadrant had *none* of its hundred members inside the axes. A domain has to
 * be supplied or most of the space is off the picture.
 *
 * And the axes share a scale, so a unit of x has to be drawn the same size as a
 * unit of y. Both axes therefore get the same span, each centred on its own
 * data — square, and still using the canvas.
 */

export type Domain = { min: number; max: number };
export type ViewBox = { x: Domain; y: Domain };

/** Enough of a span that a single point does not produce a zero-width axis. */
const MIN_SPAN = 1e-6;

export const viewBox = (
  points: ExplorerPoint[],
  pad = 0.06,
): ViewBox | null => {
  if (points.length === 0) return null;

  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;

  points.forEach((p) => {
    if (p.x < xMin) xMin = p.x;
    if (p.x > xMax) xMax = p.x;
    if (p.y < yMin) yMin = p.y;
    if (p.y > yMax) yMax = p.y;
  });

  // One span for both axes: the larger of the two, so nothing is cropped, and
  // the same on each so distance reads the same in both directions.
  const span = Math.max(xMax - xMin, yMax - yMin, MIN_SPAN) * (1 + pad * 2);
  const cx = (xMin + xMax) / 2;
  const cy = (yMin + yMax) / 2;

  return {
    x: { min: cx - span / 2, max: cx + span / 2 },
    y: { min: cy - span / 2, max: cy + span / 2 },
  };
};

/**
 * Points inside the window, and a count of those left out.
 *
 * nivo's canvas renderer draws every node it is given — `renderNode` is an
 * `arc` with no `clip()` — so a point outside the domain is painted over the
 * axes and the margin rather than hidden. Filtering here is what makes a
 * focused view possible at all, and it is also what makes it cheap: focusing on
 * a norm set draws hundreds of points instead of sixteen thousand.
 */
export const withinBox = (
  points: ExplorerPoint[],
  box: ViewBox | null,
): { visible: ExplorerPoint[]; offscreen: number } => {
  if (!box) return { visible: points, offscreen: 0 };

  const visible = points.filter(
    (p) =>
      p.x >= box.x.min &&
      p.x <= box.x.max &&
      p.y >= box.y.min &&
      p.y <= box.y.max,
  );

  return { visible, offscreen: points.length - visible.length };
};

/** The points a focused view is fitted to: the anchors and the members. */
export const markedPoints = (points: ExplorerPoint[]): ExplorerPoint[] =>
  points.filter((p) => p.state === 'anchor' || p.state === 'included');
